import { useCallback, useEffect, useState } from 'react'
import { GridRowId } from '@mui/x-data-grid-pro'
import { useBulkActionContext } from '../../../BulkActionContext'
import { bulkActionErrors } from '../../../constants'
import { BulkReopenDTO } from '../../../typings/BulkReopen'
import { ErrorSummary, ValidationCallback } from '../typings'
import { produce } from 'immer'

function formatErrors(dto?: BulkReopenDTO) {
  const errors = new Map<GridRowId, string[]>()
  if (!!dto) {
    const { alreadyOpenEntities, closedWonEntities } = dto

    const closed = closedWonEntities === null ? [] : closedWonEntities
    const open = alreadyOpenEntities === null ? [] : alreadyOpenEntities
    const hasAlreadyOpen = open.length > 0
    const hasClosedWon = closed.length > 0
    closed.map((e) => {
      errors.set(e.entityId, [bulkActionErrors.reopen.closedWon.rowError])
    })
    open.map((e) => {
      errors.set(e.entityId, [bulkActionErrors.reopen.open.rowError])
    })

    return {
      errors,
      hasAlreadyOpen,
      hasClosedWon,
    }
  }
  return {
    errors,
    hasAlreadyOpen: false,
    hasClosedWon: false,
  }
}

export const useValidateRows = (validateRows: ValidationCallback) => {
  const {
    selectedRows,
    dispatch,
    state: { isValidating },
  } = useBulkActionContext()

  const [errorSummary, setErrorSummary] = useState<ErrorSummary>({
    count: 0,
    hasAlreadyOpen: false,
    hasClosedWon: false,
  })

  const updateState = useCallback(
    (dto?: BulkReopenDTO) => {
      const { errors, hasAlreadyOpen, hasClosedWon } = formatErrors(dto)
      dispatch({ type: 'setErrors', payload: errors })
      setErrorSummary((prev) =>
        produce(prev, (draft) => {
          draft.count = errors.size
          draft.hasAlreadyOpen = hasAlreadyOpen
          draft.hasClosedWon = hasClosedWon
        }),
      )
    },
    [dispatch],
  )

  useEffect(() => {
    let mounted = true

    const doValidation = async () => {
      dispatch({ type: 'startValidating' })

      const dto = await validateRows({
        ids: selectedRows.map((id) => Number(id)),
        expectedClosingDate: null,
        owner: null,
      })

      if (mounted) updateState(dto)
    }
    const shouldValidate = selectedRows.length > 0
    if (shouldValidate) doValidation()
    else updateState()

    return () => {
      mounted = false
    }
  }, [dispatch, selectedRows, updateState, validateRows])

  return {
    errorSummary,
    loading: isValidating,
  }
}
